import { useState } from "react";
import { ArrowRight, LockKey, ShieldStar } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const LoginScreen = ({ onLogin, loading }) => {
  const [password, setPassword] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!password.trim()) return;
    const result = await onLogin(password);
    if (!result.ok) setPassword("");
  };

  return (
    <main
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] px-6 py-12"
      data-testid="admin-login-screen"
    >
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1614064641936-a59265462328?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-20" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#d4af3715,transparent_45%),linear-gradient(180deg,rgba(5,5,5,.6),rgba(5,5,5,.95))]" />

      <section className="relative grid w-full max-w-6xl gap-10 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
        <div className="space-y-6">
          <p className="text-xs uppercase tracking-[0.34em] text-[#D4AF37]">Saniya Prime Terminal</p>
          <h1 className="max-w-3xl font-['JetBrains_Mono'] text-4xl font-black leading-none tracking-tighter text-zinc-100 sm:text-5xl lg:text-6xl">
            Luxury command for escrow, confirmations, and payout control.
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-zinc-400">
            Built like a private trading desk: dark metal surfaces, live operator intelligence, and a single secure entry for your bot operations.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              ["Live desks", "26 active tickets"],
              ["Settlement lane", "2-step payout control"],
              ["Monitoring", "Wallet + release health"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="border border-white/10 bg-black/40 p-4 backdrop-blur-sm"
                data-testid={`login-highlight-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">{label}</p>
                <p className="mt-3 font-['JetBrains_Mono'] text-lg text-zinc-100">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="border border-white/10 bg-black/60 p-8 backdrop-blur-2xl"
          data-testid="admin-login-form"
        >
          <div className="mb-8 flex items-center justify-between border-b border-white/10 pb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-zinc-500">Private admin access</p>
              <h2 className="mt-3 font-['JetBrains_Mono'] text-2xl font-bold tracking-tight text-zinc-100">Unlock operator dashboard</h2>
            </div>
            <div className="flex h-12 w-12 items-center justify-center border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]">
              <ShieldStar size={24} weight="duotone" />
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-xs uppercase tracking-[0.26em] text-zinc-500">Access key</label>
            <div className="relative">
              <LockKey className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]" size={18} />
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter admin password"
                className="h-14 border-white/20 bg-[#0A0A0A] pl-12 font-['JetBrains_Mono'] text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[#D4AF37]/30"
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between text-xs uppercase tracking-[0.22em] text-zinc-500">
            <span data-testid="login-note">Single-admin access only</span>
            <span data-testid="login-encryption-label">Secured session</span>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="mt-8 h-14 w-full rounded-none border border-[#D4AF37] bg-[#D4AF37] font-['JetBrains_Mono'] text-sm uppercase tracking-[0.22em] text-black hover:bg-[#E6C15A]"
            data-testid="admin-login-submit-button"
          >
            {loading ? "Unlocking" : "Enter terminal"}
            <ArrowRight size={18} weight="bold" />
          </Button>
        </form>
      </section>
    </main>
  );
};
